import { Injectable } from '@angular/core';
import { Papa } from 'ngx-papaparse';
import {
  SupHashRemovalRow,
  SupHashRemovalValidationError,
  SupHashRemovalValidationResult,
} from './data-quality-settings.interface';

interface ParsedSupHashRemovalRow {
  PSID?: string;
  supHashId?: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class SupHashRemovalCsvValidatorService {
  static readonly MAX_ROWS = 50;
  private readonly _requiredHeaders = ['PSID', 'supHashId'];

  constructor(private _papa: Papa) {}

  validate(file: File): Promise<SupHashRemovalValidationResult> {
    return new Promise((resolve) => {
      this._papa.parse(file, {
        header: true,
        dynamicTyping: false,
        skipEmptyLines: 'greedy',
        transformHeader: (header: string) =>
          header.replace(/^\uFEFF/, '').trim(),
        complete: (result) => resolve(this._validateResult(result)),
        error: () =>
          resolve({
            totalRows: 0,
            validRows: [],
            rowErrors: [],
            fatalErrors: [
              {
                message:
                  'The CSV file could not be read. Please verify the file and try again.',
              },
            ],
          }),
      });
    });
  }

  private _validateResult(result: any): SupHashRemovalValidationResult {
    const rows = (result.data || []) as ParsedSupHashRemovalRow[];
    const headers = (result.meta?.fields || []) as string[];
    const fatalErrors = this._getFileErrors(headers, rows.length);

    if (fatalErrors.length) {
      return {
        totalRows: rows.length,
        validRows: [],
        rowErrors: [],
        fatalErrors,
      };
    }

    const parserValidation = this._groupParserErrors(result.errors || []);
    fatalErrors.push(...parserValidation.fatalErrors);
    const { validRows, rowErrors } = this._validateRows(
      rows,
      parserValidation.rowErrors
    );

    if (!validRows.length) {
      fatalErrors.push({
        message:
          'The CSV file does not contain any valid PSID and supHashId pairs.',
      });
    }

    return {
      totalRows: rows.length,
      validRows,
      rowErrors,
      fatalErrors,
    };
  }

  private _getFileErrors(headers: string[], rowCount: number) {
    const errors: SupHashRemovalValidationError[] = [];
    if (!headers.length && rowCount === 0) {
      errors.push({ message: 'The CSV file is empty.' });
    }

    const missingHeaders = this._requiredHeaders.filter(
      (header) => !headers.includes(header)
    );
    if (missingHeaders.length) {
      const suffix = missingHeaders.length > 1 ? 's' : '';
      errors.push({
        message: `Missing required header${suffix}: ${missingHeaders.join(
          ', '
        )}.`,
      });
    }

    const duplicateHeaders = headers.filter(
      (header, index) => headers.indexOf(header) !== index
    );
    if (duplicateHeaders.length) {
      const suffix = duplicateHeaders.length > 1 ? 's' : '';
      errors.push({
        message: `Duplicate header${suffix}: ${[
          ...new Set(duplicateHeaders),
        ].join(', ')}.`,
      });
    }

    if (rowCount === 0 && headers.length > 0) {
      errors.push({
        message: 'The CSV file does not contain any data rows.',
      });
    }

    if (rowCount > SupHashRemovalCsvValidatorService.MAX_ROWS) {
      errors.push({
        message: `The CSV file contains ${rowCount} rows. A maximum of ${SupHashRemovalCsvValidatorService.MAX_ROWS} data rows is allowed.`,
      });
    }
    return errors;
  }

  private _groupParserErrors(errors: any[]) {
    const rowErrors = new Map<number, string[]>();
    const fatalErrors: SupHashRemovalValidationError[] = [];
    for (const error of errors) {
      const rowIndex = Number(error.row);
      if (Number.isNaN(rowIndex) || rowIndex < 0) {
        fatalErrors.push({ message: error.message });
        continue;
      }
      const messages = rowErrors.get(rowIndex) || [];
      messages.push(error.message);
      rowErrors.set(rowIndex, messages);
    }
    return { rowErrors, fatalErrors };
  }

  private _validateRows(
    rows: ParsedSupHashRemovalRow[],
    parserErrors: Map<number, string[]>
  ) {
    const validRows: SupHashRemovalRow[] = [];
    const rowErrors: SupHashRemovalValidationError[] = [];
    const seenPairs = new Set<string>();

    rows.forEach((row, rowIndex) => {
      const rowNumber = rowIndex + 2;
      const psid = String(row.PSID ?? '').trim();
      const supHashId = String(row.supHashId ?? '').trim();
      const errors = [...(parserErrors.get(rowIndex) || [])];

      if (!psid) {
        errors.push('PSID is required.');
      }
      if (!supHashId) {
        errors.push('supHashId is required.');
      }

      const pairKey = JSON.stringify([psid, supHashId]);
      if (psid && supHashId && seenPairs.has(pairKey)) {
        errors.push('Duplicate PSID and supHashId pair.');
      }

      if (errors.length) {
        for (const message of [...new Set(errors)]) {
          rowErrors.push({ rowNumber, message });
        }
        return;
      }

      seenPairs.add(pairKey);
      validRows.push({ rowNumber, PSID: psid, supHashId });
    });
    return { validRows, rowErrors };
  }
}
